/* eslint-disable camelcase */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-console */
import { takeLatest, put, call, select } from 'redux-saga/effects';
import { push } from 'react-router-redux';
import { delay } from 'redux-saga';
import { uniq, compact } from 'lodash';
import moment from 'moment';
import { setNotification, operatorTyping, searchByOrOperator } from 'utils';
import { change, arrayRemove, arrayInsert, arrayPush, formValueSelector } from 'redux-form';
import * as OrderApi from '../api/orderApi';
import { userDataApi } from '../api/layoutApi';
import { getCustomerInvoicesApi } from '../api/customerApi';
import { toggleOrderItemStatusApi } from '../api/orderItemApi';
import * as OrderTypes from '../types/orderTypes';
import * as Macros from './macros';
import * as OrderActions from '../actions/orderActions';
import { getUserDataRes, getUserDataErr } from '../actions/layoutActions';
import { loadShippingAddressesSaga, loadEditableCustomerSaga, loadCustomerTurnoverSaga } from './customerSaga';

const selector = formValueSelector('orderForm');

const getLayout = state => state.layout;
const getOrder = state => state.order;
const getOrderItems = state => selector(state, 'orderItems');
const getCustomer = state => state.customer;

// L O A D   P A Y M E N T   T Y P E S  O R D E R
function* loadPaymentTypesSaga(action) {
    try {
        const params = { filter: JSON.stringify(action.data) };
        const result = yield OrderApi.getPaymentTypesApi(params);
        if (result.status === 200) {
            yield put(OrderActions.loadPaymentTypesRes(result.data));
        }
    } catch (e) {
        console.log(e);
        yield put(OrderActions.loadPaymentTypesErr());
    }
}

export function* loadPaymentTypesWatcher() {
    yield takeLatest(OrderTypes.LOAD_PAYMENT_TYPES, loadPaymentTypesSaga);
}
// L O A D   P A Y M E N T   T Y P E S  O R D E R

// L O A D   O R D E R   I N V O I C E S
function* loadOrderInvoicesSaga(payload) {
    try {
        const params = {
            filter: JSON.stringify({ orders_id: payload.orderId }),
            offset: 0,
            limit: 999,
        };
        const result = yield getCustomerInvoicesApi(params);

        if (result.status === 200) {
            yield put(OrderActions.loadOrderInvoicesRes(result.data));
        }
    } catch (e) {
        console.log(e);
        yield put(OrderActions.loadOrderInvoicesErr());
    }
}

export function* loadOrderInvoicesWatcher() {
    yield takeLatest(OrderTypes.LOAD_ORDER_INVOICES, loadOrderInvoicesSaga);
}
// L O A D   O R D E R   I N V O I C E S

// L O A D   O R D E R
function* loadOrderMacros(data) {
    try {
        const order = data;
        const customer = yield select(getCustomer);

        if (
            customer.addresses.shipping.listing === null
            || customer.addresses.shipping.listing?.hits.length === 0
            || customer.addresses.shipping.listing?.hits[0].customer_id !== order.customer_id
        ) {
            yield call(loadShippingAddressesSaga, { customerId: order.customer_id, setLimit: 300 });
        }

        let userIDS = [order.created_user, order.updated_by, order.sales_man_id];
        let userPaymentIDS = [];
        const userPaymentRequestIDS = [];
        let configurationIDS = [];
        let addressIDS = [];
        let productGroupIDS = [];
        let commentUserIDS = [];
        let reprintUserIDS = [];
        let paymentIDS = [];
        let packageIDS = [];

        order.items.forEach((orderItem) => {
            userIDS.push(orderItem.created_user);
            orderItem.states.forEach((state) => {
                userIDS.push(state.updated_by);
            });
            productGroupIDS.push(orderItem.product_group_id);
            reprintUserIDS = orderItem.reprint_details.map(reprint => reprint.created_by);
            commentUserIDS = [...commentUserIDS, ...orderItem.comments.map(comment => comment.created_by), ...orderItem.comments.flatMap(comment => comment.receivers)];
            configurationIDS = [...configurationIDS, ...(orderItem.configurations || []).map(configuration => configuration.configuration_id)];
            addressIDS = [...addressIDS, ...orderItem.shipping_details.map(address => address.address_id)];
            packageIDS = [...packageIDS, orderItem.package_id];
        });

        if (order.payments) {
            userPaymentIDS = order.payments.map(payment => payment.created_by);
            paymentIDS = order.payments.map(payment => payment.payment_type_id);
        }

        if (order.payment_requests) {
            order.payment_requests.forEach((paymentReq) => {
                userPaymentRequestIDS.push(paymentReq.created_by);
            });
        }

        productGroupIDS = uniq(productGroupIDS);
        userIDS = uniq(compact([...userIDS, ...commentUserIDS, ...reprintUserIDS, ...userPaymentIDS, userPaymentRequestIDS]));
        configurationIDS = uniq(configurationIDS);
        addressIDS = uniq(addressIDS);
        packageIDS = uniq(packageIDS);

        const macroTemplates = yield Macros.getTemplatesByPackagesApi(packageIDS);
        const templates = macroTemplates.data.hits;

        const templatePackages = templates.map(t => ({ id: t._id, packages: t.packages_id }));

        order.items = order.items.map(item => ({
            ...item,
            template_id: templatePackages.find(tp => tp.packages.includes(item.package_id))?.id || '1',
        }));

        yield put(OrderActions.loadOrderRes(order));

        const macroProductGroups = yield Macros.getProductGroupsMacro(productGroupIDS);
        const macroUsers = yield Macros.getUsersMacro(userIDS);
        const macroConfigurations = yield Macros.getConfigurationsMacro(configurationIDS);
        const macroContact = yield Macros.getContactsMacro(order.contact_id);
        const macroPaymentTypes = yield Macros.getPaymentTypesMacro(paymentIDS);
        const macroAddresses = yield Macros.getAddressesMacro(addressIDS);

        const users = macroUsers?.data?.hits || [];
        const configurations = macroConfigurations?.data?.hits || [];
        const productGroups = macroProductGroups?.data?.hits || [];
        const contact = macroContact?.data?.hits || [];
        const payments = macroPaymentTypes?.data?.hits || [];
        const addresses = macroAddresses?.data?.hits || [];

        yield put(OrderActions.setNeededData({
            users,
            configurations,
            productGroups,
            contact,
            payments,
            addresses,
        }));
    } catch (e) {
        yield put(OrderActions.loadOrderErr());
        console.log(e);
    }
}

function* loadOrderSaga(action) {
    try {
        const result = yield OrderApi.getOrderApi({ filter: JSON.stringify({ order_number: action.id }) });

        if (result.status === 200 && result.data.hits.length > 0) {
            const [order] = result.data.hits;

            if (order.copy) {
                let copy = null;
                if (!action.copy) {
                    const copyResult = yield OrderApi.getOrderApi({ filter: JSON.stringify({ _id: order.copy }) });
                    [copy] = copyResult.data.hits;
                } else {
                    // eslint-disable-next-line prefer-destructuring
                    copy = action.copy;
                }

                const isStillNew = moment().diff(order.created_at.$date, 'minutes') < 5;

                if (copy?.items.length !== order.items.length && isStillNew) {
                    yield delay(5000);
                    yield call(loadOrderSaga, { ...action, copy });
                } else {
                    yield call(loadOrderMacros, order);
                }
            } else {
                yield call(loadOrderMacros, order);
            }
        } else {
            yield put(push('/404'));
        }
    } catch (e) {
        yield put(OrderActions.loadOrderErr());
        console.log(e);
    }
}

export function* loadOrderWatcher() {
    yield takeLatest(OrderTypes.LOAD_ORDER, loadOrderSaga);
}
// L O A D   O R D E R

// A T T A C H   &   D E T A C H   A D D R E S S   O R D E R   I T E M
function* attachAddressToOrderItem(addresses, itemId, orderId, clonedItemId) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const bulkedAddresses = [];

        if (addresses !== null) {
            addresses.forEach((address) => {
                bulkedAddresses.push({
                    item_id: itemId,
                    address_id: address.address_id,
                    quantity: address.quantity,
                });
            });
        }

        const paramsAddresses = { bulk: bulkedAddresses };

        const result = yield OrderApi.attachAddressToOrderItemApi(paramsAddresses);

        if (result.status === 202) {
            if (clonedItemId) {
                yield put(OrderActions.cloneOrderItemRes(clonedItemId));
                setNotification('success', locale, 'global.success', 'order.notification.cloneItemSuccess');
                yield delay(1000);
                yield put({ type: OrderTypes.LOAD_ORDER, id: orderId });
            }
        } else {
            setNotification('error', locale, 'global.error', 'order.notification.cloneItemError');
        }
    } catch (e) {
        yield put(OrderActions.cloneOrderItemErr());
        console.log(e);
    }
}

function* detachAddressFromOrderItem(addresses, itemId) {
    try {
        const bulkedAddresses = [];

        addresses.forEach((address) => {
            bulkedAddresses.push({
                item_id: itemId,
                address_id: address,
            });
        });

        const paramsAddresses = { bulk: bulkedAddresses };

        yield OrderApi.detachAddressFromOrderItemApi(paramsAddresses);
    } catch (e) {
        console.log(e);
    }
}
// A T T A C H   &   D E T A C H   A D D R E S S   O R D E R   I T E M


// C L O N E   O R D E R   I T E M
function* cloneOrderItemSaga(action) {
    try {
        const layout = yield select(getLayout);
        const { locale } = layout;

        const result = yield OrderApi.addOrderItemApi(action.data);
        if (result.status === 202) {
            if (action.addresses.length > 0) {
                yield call(attachAddressToOrderItem, action.addresses, action.data.item_id, action.orderId, action.clonedItemId);
            } else {
                yield put(OrderActions.cloneOrderItemRes(action.clonedItemId));
                setNotification('success', locale, 'global.success', 'order.notification.cloneItemSuccess');
                yield delay(1000);
                yield put({ type: OrderTypes.LOAD_ORDER, id: action.orderId });
            }
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(OrderActions.cloneOrderItemErr());
        }
    } catch (e) {
        yield put(OrderActions.cloneOrderItemErr());
        console.log(e);
    }
}

export function* cloneOrderItemWatcher() {
    yield takeLatest(OrderTypes.CLONE_ORDER_ITEM, cloneOrderItemSaga);
}
// C L O N E   O R D E R   I T E M


// D E L E T E   O R D E R   I T E M
function* deleteOrderItemSaga(action) {
    const layout = yield select(getLayout);
    const customer = yield select(getCustomer);
    const { locale } = layout;

    try {
        const params = { item_id: action.orderItemId };

        const result = yield OrderApi.deleteOrderItemApi(params);
        if (result.status === 202) {
            yield put(OrderActions.deleteOrderItemRes(action.orderItemId));
            setNotification('success', locale, 'global.success', 'order.notification.deleteItemSuccess');
            yield delay(1000);

            if (action.orderReference) {
                yield call(loadOrderSaga, { id: action.orderReference });
                yield call(loadEditableCustomerSaga, { id: customer.edit.data._id });
                yield call(loadCustomerTurnoverSaga, { id: customer.edit.data._id });
            } else {
                const orderItems = yield select(getOrderItems);
                yield put(change('orderForm', 'orderItems', orderItems.filter(o => o.item_id !== action.orderItemId)));
            }
        } else {
            setNotification('error', locale, 'global.error', 'order.notification.deleteItemError');
        }
    } catch (e) {
        console.log(e);
        yield put(OrderActions.deleteOrderItemErr());
        setNotification('error', locale, 'global.error', 'order.notification.deleteItemError');
    }
}

export function* deleteOrderItemWatcher() {
    yield takeLatest(OrderTypes.DELETE_ORDER_ITEM, deleteOrderItemSaga);
}
// D E L E T E   O R D E R   I T E M


// R E P R I N T   O R D E R   I T E M
function* reprintOrderItemSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;
    const order = yield select(getOrder);

    try {
        const param = {
            item_id: action.orderItemId,
            reason: action.inputReason,
            description: action.inputTextArea,
        };

        const result = yield OrderApi.reprintOrderItemApi(param);
        if (result.status === 202) {
            yield put(OrderActions.reprintOrderItemRes(true, false));
            yield put(OrderActions.reprintOrderItemRes(false, true));
            yield delay(1000);
            yield put(OrderActions.reprintOrderItemRes(false, false));
            setNotification('success', locale, 'global.success', 'order.notification.reprintItemSuccess');
            yield call(loadOrderSaga, { id: order.view.data.order_number });
        } else {
            const error = yield result.json();

            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(OrderActions.reprintOrderItemErr(error.message));
        }
    } catch (e) {
        console.log(e);
        setNotification('error', locale, 'global.error', 'order.notification.reprintItemError');
        yield put(OrderActions.reprintOrderItemErr());
    }
}

export function* reprintOrderItemWatcher() {
    yield takeLatest(OrderTypes.REPRINT_ORDER_ITEM, reprintOrderItemSaga);
}
// R E P R I N T   O R D E R   I T E M


// C A N C E L   O R D E R
function* cancelOrderSaga(action) {
    try {
        const layout = yield select(getLayout);
        const { locale, userData } = layout;
        const param = {
            order_id: action.orderId,
            status: 'CANCELED',
            user_id: userData.data._id,
        };

        const result = yield OrderApi.changeStatusOrderApi(param);
        if (result.status === 202) {
            yield put(OrderActions.cancelOrderRes(true, false));
            yield put(OrderActions.cancelOrderRes(false, true));
            yield delay(1000);
            yield put(OrderActions.cancelOrderRes(false, false));
            setNotification('success', locale, 'global.success', 'order.notification.cancelOrderSuccess');
            yield put({ type: OrderTypes.LOAD_ORDER, id: action.orderReference });
        } else {
            yield put(OrderActions.cancelOrderRes(false, false));
            setNotification('error', locale, 'global.error', 'order.notification.cancelOrderError');
        }
    } catch (e) {
        console.log(e);
        yield put(OrderActions.cancelOrderErr());
    }
}

export function* cancelOrderWatcher() {
    yield takeLatest(OrderTypes.CANCEL_ORDER, cancelOrderSaga);
}
// C A N C E L   O R D E R


// C L O N E   O R D E R
function* cloneOrderSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const result = yield OrderApi.cloneOrderApi(action.data);
        if (result.status === 202) {
            yield put(OrderActions.cloneOrderRes(true, false));
            yield put(OrderActions.cloneOrderRes(false, true));
            yield delay(1000);

            const resultOrder = yield OrderApi.getOrderApi({ filter: JSON.stringify({ _id: action.data.order_id }) });
            if (resultOrder.status === 200) {
                yield put(push(`/crm/orders/view/${resultOrder.data.hits[0].order_number}`));
            }

            yield put(OrderActions.cloneOrderRes(false, false));
            setNotification('success', locale, 'global.success', 'order.notification.cloneOrderSuccess');
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', 'order.notification.cloneOrderError');
            yield put(OrderActions.cloneOrderErr(error.message));
        }
    } catch (e) {
        console.log(e);
        setNotification('error', locale, 'global.error', 'order.notification.cloneOrderError');
        yield put(OrderActions.cloneOrderErr());
    }
}

export function* cloneOrderWatcher() {
    yield takeLatest(OrderTypes.CLONE_ORDER, cloneOrderSaga);
}
// C L O N E   O R D E R

// E D I T   S H I P P I N G   T Y P E
function* editShippingTypesOrderSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const order = yield select(getOrder);
        const result = yield OrderApi.editShippingTypesApi(action.data);

        if (result.status === 202) {
            yield put(OrderActions.editShippingTypesOrderRes(false, false));

            setNotification('success', locale, 'global.success', 'order.notification.shippingTypeSuccess');
            yield call(loadOrderSaga, { id: order.view.data.order_number });
        } else {
            yield put(OrderActions.editShippingTypesOrderErr());
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });

            yield delay(1000);
            yield put(OrderActions.editShippingTypesOrderRes(false, false));
        }
    } catch (e) {
        yield put(OrderActions.editShippingTypesOrderErr());

        yield delay(1000);
        yield put(OrderActions.editShippingTypesOrderRes(false, false));
        console.log(e);
    }
}

export function* editShippingTypesOrderWatcher() {
    yield takeLatest(OrderTypes.EDIT_SHIPPING_TYPE_ORDER, editShippingTypesOrderSaga);
}
// E D I T   S H I P P I N G   T Y P E

// O R D E R   T O   R E C E I V E D
function* orderToReceiveSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const order = yield select(getOrder);
        const result = yield OrderApi.changeStatusOrderApi(action.data);
        if (result.status === 202) {
            yield put(OrderActions.orderToReceiveRes(true, false));
            yield put(OrderActions.orderToReceiveRes(false, true));
            yield delay(1000);
            yield put(OrderActions.orderToReceiveRes(false, false));
            setNotification('success', locale, 'global.success', 'order.notification.orderToReceivedSuccess');
            yield call(loadOrderSaga, { id: order.view.data.order_number });
            yield call(loadEditableCustomerSaga, { id: order.view.data.customer_id });
            yield call(loadCustomerTurnoverSaga, { id: order.view.data.customer_id });
        } else {
            setNotification('error', locale, 'global.error', 'order.notification.orderToReceivedError');
            yield put(OrderActions.orderToReceiveErr());
        }
    } catch (e) {
        console.log(e);
        yield put(OrderActions.orderToReceiveErr());
    }
}

export function* orderToReceiveWatcher() {
    yield takeLatest(OrderTypes.ORDER_TO_RECEIVED, orderToReceiveSaga);
}
// O R D E R   T O   R E C E I V E D

// S E T   C O M M E N T   O R D E R  I T E M S
function* commentOrderItemSaga(action) {
    try {
        const layout = yield select(getLayout);
        const order = yield select(getOrder);

        const { locale } = layout;

        const result = yield OrderApi.commentOrderItemApi(action.data);
        if (result.status === 202) {
            yield put(OrderActions.setOrderItemCommentRes(true, false));
            yield put(OrderActions.setOrderItemCommentRes(false, true));
            yield delay(1000);
            yield put(OrderActions.setOrderItemCommentRes(false, false));
            setNotification('success', locale, 'global.success', 'order.notification.commentItemSuccess');
            yield call(loadOrderSaga, { id: order.view.data.order_number });
        } else {
            setNotification('error', locale, 'global.error', 'order.notification.commentItemError');
        }
    } catch (e) {
        console.log(e);
        yield put(OrderActions.setOrderItemCommentErr());
    }
}

export function* commentOrderItemWatcher() {
    yield takeLatest(OrderTypes.SET_COMMENT_ORDER_ITEMS, commentOrderItemSaga);
}
// S E T   C O M M E N T   O R D E R  I T E M S


// A D D   L I N K   O R D E R   I T E M S
function* linkOrderItemSaga(action) {
    try {
        const layout = yield select(getLayout);
        const order = yield select(getOrder);

        const { locale } = layout;
        const result = yield OrderApi.linkOrderItemApi(action.data);
        if (result.status === 202) {
            yield put(OrderActions.addOrderItemLinkRes(true, false));
            yield put(OrderActions.addOrderItemLinkRes(false, true));
            yield delay(1000);
            yield put(OrderActions.addOrderItemLinkRes(false, false));
            setNotification('success', locale, 'global.success', 'order.notification.linkItemSuccess');
            yield call(loadOrderSaga, { id: order.view.data.order_number });
        } else {
            setNotification('error', locale, 'global.error', 'order.notification.linkOrderItemError');
        }
    } catch (e) {
        console.log(e);
        yield put(OrderActions.addOrderItemLinkErr());
    }
}

export function* linkOrderItemWatcher() {
    yield takeLatest(OrderTypes.ADD_LINK_ORDER_ITEMS, linkOrderItemSaga);
}
// A D D   L I N K   O R D E R   I T E M S


// A D D   D O C U M E N T   O R D E R   I T E M S
function* addDocumentOrderItemSaga(action) {
    try {
        const layout = yield select(getLayout);
        const order = yield select(getOrder);

        const { locale } = layout;
        const result = yield OrderApi.addDocumentOrderItemApi(action.data);

        if (result.status === 202) {
            yield put(OrderActions.addOrderItemDocumentRes(true, false));
            yield put(OrderActions.addOrderItemDocumentRes(false, true));
            yield delay(1000);
            yield put(OrderActions.addOrderItemDocumentRes(false, false));
            setNotification('success', locale, 'global.success', 'order.notification.documentItemSuccess');
            yield call(loadOrderSaga, { id: order.view.data.order_number });
        } else {
            setNotification('error', locale, 'global.error', 'order.notification.documentOrderItemError');
        }
    } catch (e) {
        console.log(e);
        yield put(OrderActions.addOrderItemDocumentErr());
    }
}

export function* addDocumentOrderItemWatcher() {
    yield takeLatest(OrderTypes.ADD_DOCUMENT_ORDER_ITEMS, addDocumentOrderItemSaga);
}
// A D D   D O C U M E N T   O R D E R   I T E M S


// D E L E T E   D O C U M E N T   O R D E R   I T E M S
function* deleteDocumentOrderItemSaga(action) {
    try {
        const layout = yield select(getLayout);
        const order = yield select(getOrder);

        const { locale } = layout;
        const result = yield OrderApi.deleteDocumentOrderItemApi(action.data);
        if (result.status === 202) {
            yield put(OrderActions.deleteOrderItemDocumentRes(true, false));
            yield put(OrderActions.deleteOrderItemDocumentRes(false, true));
            yield delay(1000);
            yield put(OrderActions.deleteOrderItemDocumentRes(false, false));
            setNotification('success', locale, 'global.success', 'order.notification.deleteDocumentItemSuccess');
            yield call(loadOrderSaga, { id: order.view.data.order_number });
        } else {
            setNotification('error', locale, 'global.error', 'order.notification.deleteDocumentOrderItemError');
        }
    } catch (e) {
        yield put(OrderActions.deleteOrderItemDocumentErr());
        console.log(e);
    }
}

export function* deleteDocumentOrderItemWatcher() {
    yield takeLatest(OrderTypes.DELETE_DOCUMENT_ORDER_ITEMS, deleteDocumentOrderItemSaga);
}
// D E L E T E   D O C U M E N T   O R D E R   I T E M S


// D E L E T E   L I N K   O R D E R  I T E M S
function* deleteLinkOrderItemSaga(action) {
    try {
        const layout = yield select(getLayout);
        const order = yield select(getOrder);

        const { locale } = layout;
        const result = yield OrderApi.deleteLinkOrderItemApi(action.data);
        if (result.status === 202) {
            yield put(OrderActions.deleteOrderItemLinkRes(true, false));
            yield put(OrderActions.deleteOrderItemLinkRes(false, true));
            yield delay(1000);
            yield put(OrderActions.deleteOrderItemLinkRes(false, false));
            setNotification('success', locale, 'global.success', 'order.notification.deleteLinkItemSuccess');
            yield call(loadOrderSaga, { id: order.view.data.order_number });
        } else {
            setNotification('error', locale, 'global.error', 'order.notification.deleteLinkOrderItemError');
        }
    } catch (e) {
        yield put(OrderActions.deleteOrderItemLinkErr());
        console.log(e);
    }
}

export function* deleteLinkOrderItemWatcher() {
    yield takeLatest(OrderTypes.DELETE_LINK_ORDER_ITEMS, deleteLinkOrderItemSaga);
}
// D E L E T E   L I N K   O R D E R  I T E M S


// A D D   N E W   O R D E R
function* addNewOrderSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const result = yield OrderApi.addNewOrderApi(action.data);

        if (result.status === 202) {
            const resultAttach = yield OrderApi.attachBillingAccountToOrderApi({
                order_id: action.data.order_id,
                address_id: action.data.billing_account_id,
            });

            if (resultAttach.status === 202) {
                yield put(OrderActions.addNewOrderRes(action.data));

                if (action.data.neutral) {
                    yield OrderApi.editShippingTypesApi({
                        order_id: action.data.order_id,
                        shipping_type_id: action.data.neutral,
                        neutral_shipping: true,
                    });
                }

                const params = { filter: JSON.stringify({ _id: action.data.order_id }) };
                const resultOrder = yield OrderApi.getOrderApi(params);

                if (resultOrder.status === 200) {
                    yield put(push(`/crm/orders/view/${resultOrder.data.hits[0].order_number}`));
                    yield call(loadEditableCustomerSaga, { id: action.data.customer_id });
                    yield call(loadCustomerTurnoverSaga, { id: action.data.customer_id });
                    setNotification('success', locale, 'global.success', 'order.notification.addOrderSuccess');
                }
            } else {
                setNotification('error', locale, 'global.error', 'order.notification.addOrderError');
                yield put(OrderActions.addNewOrderErr());
            }
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(OrderActions.addNewOrderErr());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'order.notification.addOrderError');
        yield put(OrderActions.addNewOrderErr());
        console.log(e);
    }
}

export function* addNewOrderWatcher() {
    yield takeLatest(OrderTypes.ADD_NEW_ORDER, addNewOrderSaga);
}
// A D D   N E W   O R D E R


// A D D   N E W   O R D E R
function* editOrderSaga(action) {
    const layout = yield select(getLayout);
    const order = yield select(getOrder);
    const { locale } = layout;

    try {
        const result = yield OrderApi.editOrderApi(action.data);

        if (result.status === 202) {
            yield put(OrderActions.editOrderRes());
            yield call(loadOrderSaga, { id: order.view.data.order_number });
            setNotification('success', locale, 'global.success', 'order.notification.editOrderSuccess');
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(OrderActions.editOrderErr());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'order.notification.editOrderError');
        yield put(OrderActions.editOrderErr());
        console.log(e);
    }
}

export function* editOrderWatcher() {
    yield takeLatest(OrderTypes.EDIT_ORDER, editOrderSaga);
}
// A D D   N E W   O R D E R


// A D D   N E W   O R D E R   I T E M
function* addNewOrderItemSaga(action) {
    const layout = yield select(getLayout);
    const order = yield select(getOrder);
    const { locale } = layout;
    const { data } = action;

    try {
        const result = yield OrderApi.addOrderItemApi(data);

        if (result.status === 202) {
            yield put(OrderActions.addNewOrderItemRes(false, true));
            yield put(OrderActions.addNewOrderItemRes(false, false));

            if (action.view) {
                yield call(loadOrderSaga, { id: order.view.data.order_number });
                yield call(loadEditableCustomerSaga, { id: order.view.data.customer_id });
                yield call(loadCustomerTurnoverSaga, { id: order.view.data.customer_id });
            } else {
                const macroProductGroups = yield Macros.getProductGroupsMacro(data.product_group_id);

                const newOrderItem = data;
                [newOrderItem.product_group] = macroProductGroups?.data?.hits || [];

                yield put(arrayPush('orderForm', 'orderItems', newOrderItem));
            }

            setNotification('success', locale, 'global.success', 'order.notification.addItemSuccess');
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(OrderActions.addNewOrderItemErr());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'order.notification.addItemError');
        yield put(OrderActions.addNewOrderItemErr());
        console.log(e);
    }
}

export function* addNewOrderItemWatcher() {
    yield takeLatest(OrderTypes.ADD_NEW_ORDER_ITEM, addNewOrderItemSaga);
}
// A D D   N E W   O R D E R   I T E M

// E D I T   O R D E R   I T E M
function* editOrderItemSaga(action) {
    const layout = yield select(getLayout);
    const order = yield select(getOrder);
    const orderItems = yield select(getOrderItems);
    const { locale } = layout;
    const { data } = action;

    try {
        const result = yield OrderApi.editOrderItemApi(data);

        if (result.status === 202) {
            yield put(OrderActions.editOrderItemRes(false, true));
            yield put(OrderActions.editOrderItemRes(false, false));

            if (data.local) {
                const macroProductGroups = yield Macros.getProductGroupsMacro(data.product_group_id);

                const newOrderItem = data;
                [newOrderItem.product_group] = macroProductGroups?.data?.hits || [];

                const indexEditableItem = orderItems.findIndex(o => o.item_id === data.item_id);
                yield put(arrayRemove('orderForm', 'orderItems', indexEditableItem));
                yield put(arrayInsert('orderForm', 'orderItems', indexEditableItem, newOrderItem));
            } else {
                yield call(loadOrderSaga, { id: order.view.data.order_number });
                yield call(loadEditableCustomerSaga, { id: order.view.data.customer_id });
                yield call(loadCustomerTurnoverSaga, { id: order.view.data.customer_id });
            }

            setNotification('success', locale, 'global.success', 'order.notification.editItemSuccess');
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(OrderActions.editOrderItemErr());
        }
    } catch (e) {
        console.log(e);
        setNotification('error', locale, 'global.error', 'order.notification.editItemError');
        yield put(OrderActions.editOrderItemErr());
    }
}

export function* editOrderItemWatcher() {
    yield takeLatest(OrderTypes.EDIT_ORDER_ITEM, editOrderItemSaga);
}
// E D I T   O R D E R   I T E M

// S E T   E X P E C T E D   S H I P P I N G   D A T E
function* setExpectedShippingDateSaga(action) {
    const layout = yield select(getLayout);
    const order = yield select(getOrder);
    const { locale } = layout;

    try {
        const result = yield OrderApi.editOrderItemApi(action.data);
        if (result.status === 202) {
            yield delay(7000);
            yield put(OrderActions.setExpectedShippingDateRes(false, true));
            yield put(OrderActions.setExpectedShippingDateRes(false, false));

            yield call(loadOrderSaga, { id: order.view.data.order_number });
            setNotification('success', locale, 'global.success', 'order.notification.expectedShippingDate.add.success');
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(OrderActions.setExpectedShippingDateErr());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'order.notification.expectedShippingDate.add.error');
        yield put(OrderActions.setExpectedShippingDateErr());
        console.log(e);
    }
}

export function* setExpectedShippingDateWatcher() {
    yield takeLatest(OrderTypes.SET_EXPECTED_SHIPPING_DATE, setExpectedShippingDateSaga);
}
// S E T   E X P E C T E D   S H I P P I N G   D A T E

// A D D   A D D I T I O N A L   I T E M
function* addAdditionalItemSaga(action) {
    const layout = yield select(getLayout);
    const order = yield select(getOrder);
    const { locale } = layout;

    try {
        const result = yield OrderApi.addAdditionalItemApi(action.data);
        if (result.status === 202) {
            yield put(OrderActions.addAdditionalItemRes(false, true));
            yield put(OrderActions.addAdditionalItemRes(false, false));

            yield call(loadOrderSaga, { id: order.view.data.order_number });
            yield call(loadEditableCustomerSaga, { id: order.view.data.customer_id });
            yield call(loadCustomerTurnoverSaga, { id: order.view.data.customer_id });

            setNotification('success', locale, 'global.success', 'order.notification.additionalItem.add.success');
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(OrderActions.addAdditionalItemErr());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'order.notification.additionalItem.add.error');
        yield put(OrderActions.addAdditionalItemErr());
        console.log(e);
    }
}

export function* addAdditionalItemWatcher() {
    yield takeLatest(OrderTypes.ADD_ADDITIONAL_ITEM, addAdditionalItemSaga);
}
// A D D   A D D I T I O N A L   I T E M

// D E L E T E   A D D I T I O N A L   I T E M
function* deleteAdditionalItemSaga(action) {
    const layout = yield select(getLayout);
    const order = yield select(getOrder);
    const { locale } = layout;

    try {
        const result = yield OrderApi.deleteAdditionalItemApi(action.data);
        if (result.status === 202) {
            yield put(OrderActions.deleteAdditionalItemRes());

            yield call(loadOrderSaga, { id: order.view.data.order_number });
            yield call(loadEditableCustomerSaga, { id: order.view.data.customer_id });
            yield call(loadCustomerTurnoverSaga, { id: order.view.data.customer_id });

            setNotification('success', locale, 'global.success', 'order.notification.additionalItem.delete.success');
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(OrderActions.deleteAdditionalItemErr());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'order.notification.additionalItem.delete.error');
        yield put(OrderActions.deleteAdditionalItemErr());
        console.log(e);
    }
}

export function* deleteAdditionalItemWatcher() {
    yield takeLatest(OrderTypes.DELETE_ADDITIONAL_ITEM, deleteAdditionalItemSaga);
}
// D E L E T E   A D D I T I O N A L   I T E M

// G E T   U S E R   D A T A
function* getUserDataToOrderSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const headers = {
            Authorization: `${action.data.token_type} ${action.data.access_token}`,
            'Content-Type': 'application/json',
        };

        const result = yield userDataApi({}, headers);

        if (result.status === 200) {
            const userData = yield result.data;
            yield put(getUserDataRes(userData));
        } else if (result.status === 401) {
            yield put(getUserDataErr());
        } else if (result.status === 500) {
            yield put(getUserDataErr());
        } else {
            yield put(getUserDataErr());
        }
    } catch (e) {
        yield put(getUserDataErr());
        setNotification('error', locale, 'global.error', 'error.login.error');
        console.log(e);
    }
}
// G E T   U S E R   D A T A

//  A D D   N E W   F A V O R I S   O R D E R
function* addNewFavorisOrderSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const result = yield OrderApi.addFavorisOrderApi(action.data);
        if (result.status === 202) {
            yield put(OrderActions.addNewFavorisOrderRes(true, false));
            yield put(OrderActions.addNewFavorisOrderRes(false, true));
            yield put(OrderActions.addNewFavorisOrderRes(false, false));
            setNotification('success', locale, 'global.success', 'order.notification.addFavorisOrderSuccess', {}, { placement: 'bottomRight' });
            yield call(getUserDataToOrderSaga, { data: layout.userToken.data });
            // yield call(loadFavorisOrderSaga, {});
        } else {
            setNotification('error', locale, 'global.error', 'order.notification.addMoreThanFavoriteOrderError', {}, { placement: 'bottomRight' });
            yield put(OrderActions.addNewFavorisOrderErr());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'order.notification.addFavorisOrderError', {}, { placement: 'bottomRight' });
        yield put(OrderActions.addNewFavorisOrderErr());
        console.log(e);
    }
}

export function* addNewFavorisOrderWatcher() {
    yield takeLatest(OrderTypes.ADD_NEW_FAVORIS_ORDER, addNewFavorisOrderSaga);
}
//  A D D   N E W   F A V O R I S   O R D E R

//  D E L E T E   F A V O R I S   O R D E R
function* deleteFavorisOrderSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const result = yield OrderApi.deleteFavorisOrderApi(action.data);
        if (result.status === 202) {
            yield put(OrderActions.deleteFavorisOrderRes(true, false));
            yield put(OrderActions.deleteFavorisOrderRes(false, true));
            yield put(OrderActions.deleteFavorisOrderRes(false, false));
            setNotification('success', locale, 'global.success', 'order.notification.delFavorisOrderSuccess', {}, { placement: 'bottomRight' });
            yield call(getUserDataToOrderSaga, { data: layout.userToken.data });
            // yield call(loadFavorisOrderSaga, {});
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(OrderActions.deleteFavorisOrderErr());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'order.notification.delFavorisOrderError', {}, { placement: 'bottomRight' });
        yield put(OrderActions.deleteFavorisOrderErr());
        console.log(e);
    }
}

export function* deleteFavorisOrderWatcher() {
    yield takeLatest(OrderTypes.DELETE_FAVORIS_ORDER, deleteFavorisOrderSaga);
}
//  D E L E T E   F A V O R I S   O R D E R

// L O A D   B I L L I N G   A C C O U N T
function* loadBillingAccountSaga() {
    const customer = yield select(getCustomer);

    let customerData = {};

    if (customer.edit.data) {
        customerData = customer.edit.data;
    }

    try {
        const params = {
            filter: {
                customer_id: customerData._id,
                type: 'BILLING_ACCOUNT',
            },
            offset: 0,
            limit: 999,
        };

        params.filter = JSON.stringify(params.filter);
        params.sort_by = JSON.stringify({ created_at: -1 });

        const result = yield OrderApi.getBillingAccountApi(params);

        if (result.status === 200) {
            yield put(OrderActions.loadBillingAccountRes(result.data));
        }
    } catch (e) {
        yield put(OrderActions.loadBillingAccountErr());
        console.log(e);
    }
}

export function* loadBillingAccountWatcher() {
    yield takeLatest(OrderTypes.LOAD_BILLING_ACCOUNT, loadBillingAccountSaga);
}
// L O A D   B I L L I N G   A C C O U N T

// A D D  P A Y M E N T  O R D E R
function* addPaymentOrderSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;
    const order = yield select(getOrder);

    try {
        const result = yield OrderApi.addPaymentOrderApi(action.data);
        if (result.status === 202) {
            yield put(OrderActions.addPaymentOrderRes(true, false));
            yield put(OrderActions.addPaymentOrderRes(false, true));
            yield put(OrderActions.addPaymentOrderRes(false, false));
            setNotification('success', locale, 'global.success', 'order.notification.addPaymentSuccess');
            yield call(loadOrderSaga, { id: order.view.data.order_number });
            yield call(loadEditableCustomerSaga, { id: order.view.data.customer_id });
            yield call(loadCustomerTurnoverSaga, { id: order.view.data.customer_id });
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(OrderActions.addPaymentOrderErr());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'order.notification.addPaymentError');
        yield put(OrderActions.addPaymentOrderErr());
        console.log(e);
    }
}

export function* addPaymentOrderWatcher() {
    yield takeLatest(OrderTypes.ADD_PAYMENT_ORDER, addPaymentOrderSaga);
}
// A D D  P A Y M E N T  O R D E R

//  D E L E T E   P A Y M E N T  O R D E R
function* deletePaymentOrderSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;
    const order = yield select(getOrder);

    try {
        const result = yield OrderApi.deletePaymentOrderApi(action.data);
        if (result.status === 202) {
            yield put(OrderActions.deletePaymentOrderRes(true, false));
            yield put(OrderActions.deletePaymentOrderRes(false, true));
            yield put(OrderActions.deletePaymentOrderRes(false, false));
            setNotification('success', locale, 'global.success', 'order.notification.deletePaymentSuccess');
            yield call(loadOrderSaga, { id: order.view.data.order_number });
            yield call(loadEditableCustomerSaga, { id: order.view.data.customer_id });
            yield call(loadCustomerTurnoverSaga, { id: order.view.data.customer_id });
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(OrderActions.deletePaymentOrderErr());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'order.notification.deletePaymentError');
        yield put(OrderActions.deletePaymentOrderErr());
        console.log(e);
    }
}

export function* deletePaymentOrderWatcher() {
    yield takeLatest(OrderTypes.DELETE_PAYMENT_ORDER, deletePaymentOrderSaga);
}
//  D E L E T E   P A Y M E N T  O R D E R

// A D D  P A Y M E N T  R E Q U E S T  O R D E R
function* addPaymentRequestOrderSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;
    const order = yield select(getOrder);

    try {
        const result = yield OrderApi.addPaymentRequestOrderApi(action.data);
        if (result.status === 202) {
            yield put(OrderActions.addPaymentRequestOrderRes(true, false));
            yield put(OrderActions.addPaymentRequestOrderRes(false, true));
            yield put(OrderActions.addPaymentRequestOrderRes(false, false));
            setNotification('success', locale, 'global.success', 'order.notification.addPaymentRequestSuccess');
            yield call(loadOrderSaga, { id: order.view.data.order_number });
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(OrderActions.addPaymentRequestOrderErr());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'order.notification.addPaymentRequestError');
        yield put(OrderActions.addPaymentRequestOrderErr());
        console.log(e);
    }
}

export function* addPaymentRequestOrderWatcher() {
    yield takeLatest(OrderTypes.ADD_PAYMENT_REQUEST_ORDER, addPaymentRequestOrderSaga);
}
// A D D  P A Y M E N T  R E Q U E S T  O R D E R


// D E L E T E   P R I N T I N G   F I L E
function* deletePrintingFileSaga(action) {
    const layout = yield select(getLayout);
    const order = yield select(getOrder);
    const { locale } = layout;

    try {
        const result = yield OrderApi.deletePrintingFileApi(action.data);
        if (result.status === 202) {
            yield put(OrderActions.deletePrintingFileRes());

            yield call(loadOrderSaga, { id: order.view.data.order_number });

            setNotification('success', locale, 'global.success', 'order.printingFile.delete.success');
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(OrderActions.deletePrintingFileErr());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'order.printingFile.delete.error');
        yield put(OrderActions.deletePrintingFileErr());
        console.log(e);
    }
}

export function* deletePrintingFileWatcher() {
    yield takeLatest(OrderTypes.DELETE_PRINTING_FILE, deletePrintingFileSaga);
}
// D E L E T E   P R I N T I N G   F I L E

// C H A N G E   O R D E R   I T E M   S T A T U S
function* changeOrderItemStatusSaga(action) {
    const { locale } = yield select(getLayout);
    const order = yield select(getOrder);

    try {
        const result = yield toggleOrderItemStatusApi(action.data);

        if (result.status === 202) {
            yield put(OrderActions.changeOrderItemStatusRes(false, true));
            yield put(OrderActions.changeOrderItemStatusRes(false, false));

            yield call(loadOrderSaga, { id: order.view.data.order_number });

            setNotification('success', locale, 'global.success', 'order.notification.toggleStep.success');
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(OrderActions.changeOrderItemStatusErr());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'order.notification.toggleStep.error');
        yield put(OrderActions.changeOrderItemStatusErr());
        console.log(e);
    }
}

export function* changeOrderItemStatusWatcher() {
    yield takeLatest(OrderTypes.CHANGE_ORDER_ITEM_STATUS, changeOrderItemStatusSaga);
}
// C H A N G E   O R D E R   I T E M   S T A T U S

// C H A N G E   P R I N T I N G   O F F I C E
function* changePrintingOfficeSaga(action) {
    const order = yield select(getOrder);
    const layout = yield select(getLayout);
    const { locale } = layout;
    try {
        const result = yield OrderApi.editOrderItemApi(action.data);

        if (result.status === 202) {
            yield call(loadOrderSaga, { id: order.view.data.order_number });
            setNotification('success', locale, 'global.success', 'order.notification.changePrintingOffice.success');
        } else {
            setNotification('error', locale, 'global.error', 'order.notification.changePrintingOffice.error');
            yield put(OrderActions.hidePrintingOfficePopup());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'order.notification.changePrintingOffice.error');
        console.log(e);
    }
}

export function* changePrintingOfficeWatcher() {
    yield takeLatest(OrderTypes.CHANGE_PRINTING_OFFICE, changePrintingOfficeSaga);
}
// C H A N G E   P R I N T I N G   O F F I C E

// E D I T   S H I P P I N G   A D D R E S S
function* editShippingAddressSaga(action) {
    const layout = yield select(getLayout);
    const order = yield select(getOrder);
    const { locale } = layout;
    const { data } = action;

    try {
        yield call(detachAddressFromOrderItem, data.oldShippingAddress ? [data.oldShippingAddress] : [], data.orderItemID);
        yield call(attachAddressToOrderItem, data.mapFields, data.orderItemID, data.order_id);
        yield put(OrderActions.editShippingAddressRes(false, true));
        yield put(OrderActions.editShippingAddressRes(false, false));

        yield call(loadOrderSaga, { id: order.view.data.order_number });

        setNotification('success', locale, 'global.success', 'order.notification.editShippingAddressSuccess');
    } catch (e) {
        setNotification('error', locale, 'global.error', 'order.notification.editShippingAddressError');
        yield put(OrderActions.editShippingAddressErr());
        console.log(e);
    }
}

export function* editShippingAddressWatcher() {
    yield takeLatest(OrderTypes.EDIT_SHIPPING_ADDRESS, editShippingAddressSaga);
}
// E D I T   S H I P P I N G   A D D R E S S


// C H A N G E   B I L L I N G   A C C O U N T
function* changeBillingAccountSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const resultAttach = yield OrderApi.attachBillingAccountToOrderApi(action.data);

        if (resultAttach.status === 202) {
            yield call(loadOrderSaga, { id: action.data.orderNumber });
            yield put(OrderActions.changeBillingAccountRes());
            setNotification('success', locale, 'global.success', 'order.changeBillingAccount.success');
        } else {
            yield put(OrderActions.changeBillingAccountErr());
            setNotification('error', locale, 'global.error', 'order.changeBillingAccount.error');
        }
    } catch (e) {
        console.log(e);
    }
}

export function* changeBillingAccountWatcher() {
    yield takeLatest(OrderTypes.CHANGE_BILLING_ACCOUNT, changeBillingAccountSaga);
}
// C H A N G E   B I L L I N G   A C C O U N T

// A D D   P R I N T I N G   F I L E
function* addPrintingFileSaga(action) {
    const layout = yield select(getLayout);
    const order = yield select(getOrder);
    const { locale } = layout;
    const { printVersionData } = order.view;

    try {
        const result = yield OrderApi.addPrintingFileApi({ ...printVersionData, ...action.data });

        if (result.status === 202) {
            yield call(loadOrderSaga, { id: action.orderNumber });
            yield put(OrderActions.addPrintingFileRes());
            yield put(OrderActions.setViewDesignVariantsPopup(false));
            setNotification('success', locale, 'global.success', 'list.printVersion.printingFiles.add.success');
        } else {
            yield put(OrderActions.addPrintingFileErr());
            setNotification('error', locale, 'global.error', 'list.printVersion.printingFiles.add.error');
        }
    } catch (e) {
        yield put(OrderActions.addPrintingFileErr());
        console.log(e);
    }
}

export function* addPrintingFileWatcher() {
    yield takeLatest(OrderTypes.ADD_PRINTING_FILE, addPrintingFileSaga);
}
// A D D   P R I N T I N G   F I L E

// A D D   D I S C O U N T  I N T O   O R D E R
function* addDiscountIntoOrderSaga(action) {
    const layout = yield select(getLayout);
    const order = yield select(getOrder);
    const { locale } = layout;

    try {
        const result = yield OrderApi.addDiscountIntoOrderApi(action.data);

        if (result.status === 202) {
            yield put(OrderActions.addDiscountIntoOrderRes(false, true));
            yield put(OrderActions.addDiscountIntoOrderRes(false, false));
            yield call(loadOrderSaga, { id: order.view.data.order_number });
            yield call(loadEditableCustomerSaga, { id: order.view.data.customer_id });
            yield call(loadCustomerTurnoverSaga, { id: order.view.data.customer_id });
            setNotification('success', locale, 'global.success', 'order.notification.addDiscountSuccess');
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(OrderActions.addDiscountIntoOrderErr());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'order.notification.addDiscountError');
        yield put(OrderActions.addDiscountIntoOrderErr());
        console.log(e);
    }
}

export function* addDiscountIntoOrderWatcher() {
    yield takeLatest(OrderTypes.ADD_DISCOUNT_INTO_ORDER, addDiscountIntoOrderSaga);
}
// A D D   D I S C O U N T  I N T O   O R D E R

// C H A N G E   O R D E R   S T A T U S   T O   W A I T I N G   F O R   P A Y M E N T
function* changeOrderStatusToWaitingForPaymentSaga(action) {
    const layout = yield select(getLayout);
    const order = yield select(getOrder);
    const { locale } = layout;

    try {
        const result = yield OrderApi.changeStatusOrderApi(action.data);

        if (result.status === 202) {
            yield put(OrderActions.changeOrderStatusToWaitingForPaymentRes(false, true));
            yield put(OrderActions.changeOrderStatusToWaitingForPaymentRes(false, false));
            yield call(loadOrderSaga, { id: order.view.data.order_number });
            setNotification('success', locale, 'global.success', 'order.notification.orderToWaitingForPaymentSuccess');
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(OrderActions.changeOrderStatusToWaitingForPaymentErr());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'order.notification.orderToWaitingForPaymentError');
        yield put(OrderActions.changeOrderStatusToWaitingForPaymentErr());
        console.log(e);
    }
}

export function* changeOrderStatusToWaitingForPaymentWatcher() {
    yield takeLatest(OrderTypes.CHANGE_ORDER_STATUS_TO_WAITING_FOR_PAYMENT, changeOrderStatusToWaitingForPaymentSaga);
}
// C H A N G E   O R D E R   S T A T U S   T O   W A I T I N G   F O R   P A Y M E N T

// D O W N L O A D   O R D E R   P D F
function* downloadOrderPdfSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const result = yield OrderApi.downloadOrderPdfApi({ order_id: action.id });

        if (result.status === 202) {
            window.open(`${process.env.REACT_APP_CDN}orders/pdf-files/${action.id}/order-details.pdf`, '_blank');
            yield put(OrderActions.downloadOrderPdfRes());
        } else {
            setNotification('error', locale, 'global.error', 'order.notification.downloadOrderPdf.error');
            yield put(OrderActions.downloadOrderPdfErr());
        }
    } catch (e) {
        yield put(OrderActions.downloadOrderPdfErr());
        console.log(e);
    }
}

export function* downloadOrderPdfWatcher() {
    yield takeLatest(OrderTypes.DOWNLOAD_ORDER_PDF, downloadOrderPdfSaga);
}
// D O W N L O A D   O R D E R   P D F


// D O W N L O A D   O R D E R   I T E M   P D F
function* downloadOrderItemPdfSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const result = yield OrderApi.downloadOrderItemPdfApi({ item_id: action.id });

        if (result.status === 202) {
            window.open(`${process.env.REACT_APP_CDN}libraries/item_pdf/${action.id}.pdf`, '_blank');
            yield put(OrderActions.downloadOrderItemPdfRes());
        } else {
            setNotification('error', locale, 'global.error', 'order.notification.downloadOrderPdf.error');
            yield put(OrderActions.downloadOrderItemPdfErr());
        }
    } catch (e) {
        yield put(OrderActions.downloadOrderItemPdfErr());
        console.log(e);
    }
}

export function* downloadOrderItemPdfWatcher() {
    yield takeLatest(OrderTypes.DOWNLOAD_ORDER_ITEM_PDF, downloadOrderItemPdfSaga);
}
// D O W N L O A D   O R D E R   I T E M   P D F

// L O A D   O R D E R S
function* loadOrdersSaga() {
    try {
        const order = yield select(getOrder);
        const { offset, limit, filters, sort } = order;

        let configurationIDS = [];
        let productGroupIDS = [];
        let customerIDS = [];
        let userIDS = [];

        const params = {
            offset,
            limit,
        };

        const filterParams = {};

        if (filters.orderId !== '') {
            filterParams.order_number = operatorTyping(filters.orderId);
        }

        if (filters.reference !== '') {
            filterParams.or = searchByOrOperator(['order_reference', 'items.item_reference'], filters.reference, true);
        }

        if (filters.status !== '') {
            filterParams.status = filters.status;
        }

        if (filters.payment.length > 0) {
            filterParams['payments.payment_type_id'] = filters.payment;
        }

        if (filters.source !== undefined) {
            filterParams.source = filters.source;
        }

        if (filters.invoice_created !== undefined) {
            filterParams.invoice_created = JSON.parse(filters.invoice_created);
        }

        if (filters.amount === 'positive') {
            filterParams.amount = { gt: 0 };
        } else if (filters.amount === 'zero') {
            filterParams.amount = 0;
        }

        if (filters.shop !== undefined) {
            filterParams.reseller_id = filters.shop;
        }

        if (filters.design.length > 0) {
            filterParams['items.printing_files.design_variant_prefix'] = operatorTyping(filters.design);
        }

        if (filters.accountManager.length > 0) {
            filterParams.sales_man_id = filters.accountManager;
        }

        if (filters.createdAt.length > 0) {
            const customDate = [
                moment.utc(filters.createdAt[0]).format('YYYY-MM-DD 00:00:00'),
                moment.utc(filters.createdAt[1]).format('YYYY-MM-DD 23:59:59'),
            ];
            filterParams.created_at = { between: customDate };
        }

        if (sort !== null) {
            const sortInt = sort.type === 'asc' ? 1 : -1;
            params.sort_by = JSON.stringify({ created_at: sortInt });
        }

        params.filter = JSON.stringify(filterParams);

        const result = yield OrderApi.getOrderApi(params);

        if (result.status === 200) {
            const orders = result.data.hits;

            orders.forEach((o) => {
                if (o.sales_man_id) {
                    userIDS.push(o.sales_man_id);
                }

                o.items.forEach((item) => {
                    productGroupIDS.push(item.product_group_id);
                    customerIDS.push(o.customer_id);

                    (item.configurations || []).forEach((configuration) => {
                        configurationIDS.push(configuration.configuration_id);
                    });
                });
            });

            configurationIDS = uniq(configurationIDS);
            productGroupIDS = uniq(productGroupIDS);
            customerIDS = uniq(customerIDS);
            userIDS = uniq(userIDS);

            const macroConfigurations = yield Macros.getConfigurationsMacro(configurationIDS);
            const macroProductGroups = yield Macros.getProductGroupsMacro(productGroupIDS);
            const macroCustomers = yield Macros.getCustomersMacro(customerIDS);
            const macroUsers = yield Macros.getUsersMacro(userIDS);

            orders.forEach((ord) => {
                const accountManager = (macroUsers?.data?.hits || []).find(u => u._id === ord.sales_man_id);
                // eslint-disable-next-line no-param-reassign
                ord.accountManager = accountManager ? `${accountManager.first_name} ${accountManager.last_name}` : null;
            });

            const configurations = macroConfigurations?.data?.hits || [];
            const productGroups = macroProductGroups?.data?.hits || [];
            const customers = macroCustomers?.data?.hits || [];

            yield put(OrderActions.loadOrdersRes(result.data, {
                configurations,
                productGroups,
                customers,
            }));
        }
    } catch (e) {
        console.log(e);
        yield put(OrderActions.loadOrdersErr());
    }
}

export function* loadOrdersWatcher() {
    yield takeLatest(OrderTypes.LOAD_ORDERS, loadOrdersSaga);
}
// L O A D   O R D E R S
